import React, { useEffect, useState, useCallback, useDeferredValue } from 'react';
import { createPortal } from 'react-dom';
import { Users, MessageCircle, Bell, Search, Plus, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getFriends, getConversations, getNotifications, markAllNotificationsAsRead, clearAllNotifications, createConversation } from '../../../lib/api-client';
import { useAuth } from '../../../context/AuthContext';
import resolveAvatar from '../../../lib/resolveAvatar';
import { PanelShell } from './PanelShell';


// ---------------------------------------------------------------------------
// Friends Panel (left) — real friends list
// ---------------------------------------------------------------------------

interface Friend {
  id: number | string;
  username: string;
  displayName?: string;
  profileName?: string;
  profilePicture?: string;
  image?: string;
}

export function FriendsPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const deferredSearch = useDeferredValue(search);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const handleFriendClick = useCallback((username: string) => {
    requestAnimationFrame(() => {
      navigate(`/u/${username}`);
    });
  }, [navigate]);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const data = await getFriends();
        setFriends(data);
      } catch {
        setError('Failed to load friends');
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const filtered = deferredSearch
    ? friends.filter(f =>
        (f.displayName || f.profileName || f.username)
          .toLowerCase()
          .includes(deferredSearch.toLowerCase()),
      )
    : friends;

  return (
    <PanelShell loading={loading} error={error} empty={!user && !loading} emptyMessage="Log in to see friends">
      {/* Search with explicit inline styles */}
      <div className="p-4 pt-6 pb-3" style={{ backgroundColor: 'var(--app-bg)' }}>
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            size={18}
            style={{ color: 'var(--text-color)', opacity: 0.4 }}
          />
          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search friends..."
            className="w-full py-3 pr-4 rounded-lg text-sm outline-none"
            style={{
              backgroundColor: 'var(--card-bg)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'var(--text-color)',
              WebkitAppearance: 'none',
              paddingLeft: '2.75rem',
            }}
          />
        </div>
      </div>

      {filtered.length === 0 && !loading && (
        <div className="flex flex-col items-center gap-2 py-12 px-6">
          <Users size={32} style={{ color: 'var(--text-color)', opacity: 0.25 }} />
          <span className="text-sm" style={{ color: 'var(--text-color)', opacity: 0.4 }}>
            {deferredSearch ? 'No matches' : 'No friends yet'}
          </span>
        </div>
      )}

      {filtered.map(friend => {
        const avatar = resolveAvatar(friend.profilePicture || friend.image);
        const name = friend.displayName || friend.profileName || friend.username;

        return (
          <button
            key={friend.id}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFriendClick(friend.username);
            }}
            className="flex items-center gap-3 w-full px-4 py-3 text-left transition-colors active:bg-white/5"
            style={{ minHeight: 56 }}
          >
            {avatar ? (
              <img src={avatar} alt="" className="w-12 h-12 rounded-full object-cover pointer-events-none" loading="lazy" />
            ) : (
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold pointer-events-none"
                style={{ background: 'var(--color-accent)', color: 'var(--app-bg)' }}
              >
                {name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0 pointer-events-none">
              <span className="text-sm font-semibold block truncate" style={{ color: 'var(--text-color)' }}>
                {name}
              </span>
              <span className="text-xs" style={{ color: 'var(--text-color)', opacity: 0.4 }}>
                @{friend.username}
              </span>
            </div>
          </button>
        );
      })}

      <div className="h-24" />
    </PanelShell>
  );
}

// ---------------------------------------------------------------------------
// Messages Panel (center) — real conversations list
// ---------------------------------------------------------------------------

interface ConvPreview {
  id: string;
  name: string;
  avatar?: string;
  lastMessage: string;
  time: string;
  unread?: boolean;
}

function formatConvTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffH = (now.getTime() - d.getTime()) / 3600000;
  if (diffH < 1) return `${Math.max(1, Math.floor(diffH * 60))}m`;
  if (diffH < 24) return `${Math.floor(diffH)}h`;
  if (diffH < 168) return `${Math.floor(diffH / 24)}d`;
  return d.toLocaleDateString();
}

export function MessagesPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [convos, setConvos] = useState<ConvPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewConvo, setShowNewConvo] = useState(false);

  const handleOpenConversation = useCallback((conversationId: string) => {
    requestAnimationFrame(() => {
      navigate(`/messages/${conversationId}`);
    });
  }, [navigate]);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const data = await getConversations();
        const previews: ConvPreview[] = data.map((c: any) => {
          const otherUser = c.participants?.find((p: any) => p.id?.toString() !== user.id?.toString());
          return {
            id: c.id,
            name: c.name || otherUser?.displayName || otherUser?.username || 'Chat',
            avatar: resolveAvatar(otherUser?.profilePicture || otherUser?.image) || undefined,
            lastMessage: c.lastMessage?.text || 'No messages yet',
            time: c.updatedAt ? formatConvTime(c.updatedAt) : '',
          };
        });
        setConvos(previews);
      } catch {
        setError('Failed to load messages');
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  return (
    <PanelShell loading={loading} error={error} empty={!user && !loading} emptyMessage="Log in to see messages">
      {/* New message button */}
      {user && (
        <div className="px-4 pt-4 pb-2">
          <button
            onClick={() => setShowNewConvo(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium w-full justify-center"
            style={{
              backgroundColor: 'var(--color-accent, #06b6d4)',
              color: '#fff',
            }}
          >
            <Plus size={16} />
            New Message
          </button>
        </div>
      )}

      {convos.length === 0 && !loading && user && (
        <div className="flex flex-col items-center gap-2 py-12 px-6">
          <MessageCircle size={32} style={{ color: 'var(--text-color)', opacity: 0.25 }} />
          <span className="text-sm" style={{ color: 'var(--text-color)', opacity: 0.4 }}>
            No conversations yet
          </span>
        </div>
      )}

      {convos.map(c => (
        <button
          key={c.id}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleOpenConversation(c.id);
          }}
          className="flex items-center gap-3 w-full px-4 py-3 text-left transition-colors active:bg-white/5"
          style={{
            minHeight: 64,
            borderBottom: '1px solid rgba(255,255,255,0.04)',
          }}
        >
          {c.avatar ? (
            <img src={c.avatar} alt="" className="w-12 h-12 rounded-full object-cover pointer-events-none" loading="lazy" />
          ) : (
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold pointer-events-none"
              style={{ background: 'var(--color-accent)', color: 'var(--app-bg)' }}
            >
              {c.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0 pointer-events-none">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold truncate" style={{ color: 'var(--text-color)' }}>
                {c.name}
              </span>
              <span className="text-xs shrink-0 ml-2" style={{ color: 'var(--text-color)', opacity: 0.35 }}>
                {c.time}
              </span>
            </div>
            <span
              className="text-xs truncate block mt-0.5"
              style={{ color: 'var(--text-color)', opacity: 0.5 }}
            >
              {c.lastMessage}
            </span>
          </div>
        </button>
      ))}

      <div className="h-24" />

      {/* New conversation modal */}
      {showNewConvo && (
        <NewConversationOverlay
          onClose={() => setShowNewConvo(false)}
          onCreated={(conversationId) => {
            setShowNewConvo(false);
            navigate(`/messages/${conversationId}`);
          }}
        />
      )}
    </PanelShell>
  );
}

// ---------------------------------------------------------------------------
// New Conversation Overlay — portal overlay for creating conversations
// ---------------------------------------------------------------------------

function NewConversationOverlay({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (conversationId: string) => void;
}) {
  const { user } = useAuth();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [groupName, setGroupName] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const deferredSearch = useDeferredValue(search);

  useEffect(() => {
    (async () => {
      try {
        const data = await getFriends();
        setFriends(data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const filtered = deferredSearch
    ? friends.filter(f =>
        (f.displayName || f.profileName || f.username)
          .toLowerCase()
          .includes(deferredSearch.toLowerCase()),
      )
    : friends;

  const toggleFriend = (id: string | number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    if (!user || selectedIds.size === 0) return;
    setCreating(true);
    try {
      const participantIds = [...selectedIds].map(id => Number(id));
      participantIds.push(Number(user.id));
      const result = await createConversation({
        type: selectedIds.size > 1 ? 'group' : 'dm',
        participantIds,
        ...(selectedIds.size > 1 && groupName.trim() ? { name: groupName.trim() } : {}),
      });
      onCreated(result.id);
    } catch {
      setCreating(false);
    }
  };

  const accentColor = 'var(--color-accent, #06b6d4)';
  const textColor = 'var(--text-color, #fff)';

  return createPortal(
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 30 }}
      transition={{ type: 'spring', damping: 28, stiffness: 300 }}
      className="fixed inset-0 z-[101] flex flex-col"
      style={{ background: 'var(--app-bg, #0a0a0a)' }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div
        className="shrink-0 flex items-center justify-between px-5"
        style={{
          background: 'var(--navbar-bg, rgba(10,10,26,0.95))',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          paddingTop: 'max(env(safe-area-inset-top, 0px), 12px)',
          paddingBottom: 12,
        }}
      >
        <h2 className="text-lg font-bold" style={{ color: textColor }}>
          New Message
        </h2>
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
        >
          <X size={20} style={{ color: textColor }} />
        </button>
      </div>

      {/* Search */}
      <div className="px-5 pt-4 pb-2">
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            size={18}
            style={{ color: textColor, opacity: 0.4 }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search friends..."
            className="w-full py-3 pr-4 rounded-xl outline-none"
            style={{
              backgroundColor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: textColor,
              paddingLeft: '2.75rem',
              fontSize: 16,
            }}
          />
        </div>
      </div>

      {/* Selected users chips */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap gap-2 px-5 py-2">
          {[...selectedIds].map(id => {
            const f = friends.find(fr => fr.id === id);
            if (!f) return null;
            return (
              <button
                key={String(id)}
                onClick={() => toggleFriend(id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: accentColor, color: '#fff' }}
              >
                {f.displayName || f.profileName || f.username}
                <X size={12} />
              </button>
            );
          })}
        </div>
      )}

      {/* Group name input (when >1 selected) */}
      {selectedIds.size > 1 && (
        <div className="px-5 pb-2">
          <input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Group name (optional)"
            className="w-full py-3 px-4 rounded-xl outline-none"
            style={{
              backgroundColor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: textColor,
              fontSize: 16,
            }}
          />
        </div>
      )}

      {/* Friends list */}
      <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' as any }}>
        {loading && (
          <div className="flex items-center justify-center py-12">
            <span className="text-sm" style={{ color: textColor, opacity: 0.4 }}>Loading friends...</span>
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-12 px-6">
            <Users size={32} style={{ color: textColor, opacity: 0.25 }} />
            <span className="text-sm" style={{ color: textColor, opacity: 0.4 }}>
              {search ? 'No matches' : 'No friends yet'}
            </span>
          </div>
        )}

        {filtered.map(friend => {
          const avatar = resolveAvatar(friend.profilePicture || friend.image);
          const name = friend.displayName || friend.profileName || friend.username;
          const isSelected = selectedIds.has(friend.id);

          return (
            <button
              key={String(friend.id)}
              onClick={() => toggleFriend(friend.id)}
              className="flex items-center gap-3 w-full px-5 py-3 text-left transition-colors active:bg-white/5"
              style={{
                minHeight: 56,
                borderBottom: '1px solid rgba(255,255,255,0.04)',
              }}
            >
              {avatar ? (
                <img src={avatar} alt="" className="w-11 h-11 rounded-full object-cover" loading="lazy" />
              ) : (
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ background: accentColor, color: '#fff' }}
                >
                  {name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <span className="text-sm font-semibold block truncate" style={{ color: textColor }}>
                  {name}
                </span>
                <span className="text-xs" style={{ color: textColor, opacity: 0.4 }}>
                  @{friend.username}
                </span>
              </div>
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                style={{
                  backgroundColor: isSelected ? accentColor : 'transparent',
                  border: isSelected ? 'none' : '2px solid rgba(255,255,255,0.2)',
                }}
              >
                {isSelected && <Check size={14} style={{ color: '#fff' }} />}
              </div>
            </button>
          );
        })}
        <div className="h-24" />
      </div>

      {/* Start button */}
      <div
        className="shrink-0 px-5 pb-6 pt-3"
        style={{
          background: 'var(--app-bg, #0a0a0a)',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 24px)',
        }}
      >
        <button
          onClick={handleCreate}
          disabled={selectedIds.size === 0 || creating}
          className="w-full py-3.5 rounded-xl font-semibold text-base disabled:opacity-50"
          style={{ backgroundColor: accentColor, color: '#fff' }}
        >
          {creating ? 'Starting...' : selectedIds.size > 1 ? 'Start Group Chat' : 'Start Conversation'}
        </button>
      </div>
    </motion.div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// Activity Panel (right) — real notifications
// ---------------------------------------------------------------------------

export function ActivityPanel() {
  const { user } = useAuth();
  const [notifs, setNotifs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const data = await getNotifications(30);
        setNotifs(data.notifications || []);
      } catch {
        setError('Failed to load activity');
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* silent */ }
  }, []);

  const handleClearAll = useCallback(async () => {
    setClearing(true);
    try {
      await clearAllNotifications();
      setNotifs([]);
    } catch (err) {
      console.error('Failed to clear notifications:', err);
    } finally {
      setClearing(false);
    }
  }, []);

  return (
    <PanelShell loading={loading} error={error} empty={!user && !loading} emptyMessage="Log in to see activity">
      {/* Action buttons — always visible when logged in with notifications */}
      {user && (
        <div
          className="flex items-center justify-between px-4 py-2.5"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <button
            onClick={handleMarkAllRead}
            disabled={notifs.length === 0}
            className="text-xs font-medium"
            style={{ color: 'var(--color-accent, #06b6d4)', opacity: notifs.length === 0 ? 0.35 : 1 }}
          >
            Mark all read
          </button>
          <button
            onClick={handleClearAll}
            disabled={clearing || notifs.length === 0}
            className="text-xs font-medium"
            style={{ color: '#ef4444', opacity: (clearing || notifs.length === 0) ? 0.35 : 1 }}
          >
            {clearing ? 'Clearing...' : 'Clear all'}
          </button>
        </div>
      )}

      {notifs.length === 0 && !loading && user && (
        <div className="flex flex-col items-center gap-2 py-12 px-6">
          <Bell size={32} style={{ color: 'var(--text-color)', opacity: 0.25 }} />
          <span className="text-sm" style={{ color: 'var(--text-color)', opacity: 0.4 }}>
            No activity yet
          </span>
        </div>
      )}

      {notifs.map(n => (
        <div
          key={n.id}
          className="flex items-start gap-3 px-4 py-3"
          style={{
            borderBottom: '1px solid rgba(255,255,255,0.04)',
            background: n.read ? 'transparent' : 'rgba(255,255,255,0.02)',
          }}
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: 'var(--card-bg)' }}
          >
            <Bell size={14} style={{ color: 'var(--color-accent)' }} />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-sm block" style={{ color: 'var(--text-color)', opacity: 0.85 }}>
              {n.message || n.content || 'Notification'}
            </span>
            <span className="text-xs mt-1 block" style={{ color: 'var(--text-color)', opacity: 0.35 }}>
              {n.createdAt ? formatConvTime(n.createdAt) : ''}
            </span>
          </div>
        </div>
      ))}

      <div className="h-24" />
    </PanelShell>
  );
}
